import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AnalyticsEvent, captureAnalyticsEvent } from '../../constant/Events';
import { useAuth } from '../../hooks';
import { useCompany } from '../../hooks/useCompany';
import { useFetchCompanyDetailQuery } from '../../services/company.api';
import { useSendDelegationInvitesMutation } from '../../services/invite.api';
import { Invitee } from '../../stores/types/invites.interface';
import { Button, ButtonSize, ButtonType } from '../_ui';
import { Form } from '../_ui/Form.component';
import { useGlobalModalContext } from '../_ui/modal/GlobalModal.component';
import { InviteUser } from './InviteUser/InviteUser.component';

interface IProps {
  triggerFetchInvites: (uuid: string) => void;
}

export const TeamUserEditor = ({ triggerFetchInvites }: IProps) => {
  const { t } = useTranslation();
  const auth = useAuth();
  const { hideModal } = useGlobalModalContext();
  const [invitees, setInvitees] = useState<Invitee[]>([]);

  const [createInvite, inviteQueryResult] = useSendDelegationInvitesMutation();

  const selectedComapnyId = auth.user.attributes.profile?.lastActiveCompanyId;

  const { userScope } = useCompany();
  const { data: company } = useFetchCompanyDetailQuery({
    id: selectedComapnyId,
    scope: userScope!,
  });

  useEffect(() => {
    if (inviteQueryResult.isSuccess && company?.adminUserGroupId) {
      hideModal();
      triggerFetchInvites(company.adminUserGroupId);
    }
  }, [inviteQueryResult.isSuccess, hideModal, triggerFetchInvites, company?.adminUserGroupId]);

  const handleSaveClick = () => {
    if (company?.adminUserGroupId) {
      captureAnalyticsEvent(AnalyticsEvent.COMPANY_INVITE_SEND_EVENT);
      createInvite({
        companyId: company.id,
        invitees: invitees,
      });
    }
  };

  const isButtonEnabled = invitees.length;

  const renderInvite = () => (
    <Form onSubmit={handleSaveClick} queryResult={inviteQueryResult}>
      <InviteUser invitees={invitees} setInvitees={setInvitees} />
      <div className='flex flex-col pt-[16px] pb-[20px] sticky bottom-0 bg-white'>
        <Button
          label={t('send')}
          type={ButtonType.PRIMARY}
          disabled={!isButtonEnabled}
          size={ButtonSize.SMALL_FULL}
          isSumbit
          dataTestId='sendInviteButton'
        />
      </div>
    </Form>
  );

  return <div className='w-full h-full'>{renderInvite()}</div>;
};
